import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { 
  AlertTriangle, WifiOff, Clock, CheckCircle, RefreshCw,
  AlertCircle, Ticket, ChevronRight, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InactiveCustomerAlerts = ({ onViewCustomer, refreshInterval = 30000 }) => {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({
    total_inactive: 0,
    critical: 0,
    warning: 0,
    with_tickets: 0,
    pending_tickets: 0
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [tick, setTick] = useState(0);

  const fetchAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('monitoringToken') || localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/inactive-alerts`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAlerts(data.alerts || []);
          setSummary(data.summary || {});
          setLastUpdated(new Date());
        }
      }
    } catch (error) {
      console.error('Failed to fetch inactive alerts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchAlerts, refreshInterval]);

  // Auto-update tick every 30 seconds so downtime stays live
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  // Compute live downtime from inactive_since timestamp
  const getLiveMinutes = (alert) => {
    if (alert.inactive_since) {
      const sinceDate = new Date(alert.inactive_since.endsWith('Z') ? alert.inactive_since : alert.inactive_since + 'Z');
      if (!isNaN(sinceDate.getTime())) {
        return Math.floor((Date.now() - sinceDate.getTime()) / 60000);
      }
    }
    return alert.inactive_minutes || 0;
  };

  const handleResolve = async (customerId, e) => {
    e.stopPropagation();
    setResolving(customerId);
    try {
      const token = localStorage.getItem('monitoringToken') || localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/resolve-inactive/${customerId}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('Alert resolved successfully');
          fetchAlerts();
        } else {
          toast.error(data.message || 'Failed to resolve alert');
        }
      }
    } catch (error) {
      toast.error('Failed to resolve alert');
    } finally {
      setResolving(null);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50 border-red-200',
          text: 'text-red-700',
          badge: 'bg-red-100 text-red-800',
          icon: 'text-red-500',
          pulse: 'animate-pulse'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 border-amber-200',
          text: 'text-amber-700',
          badge: 'bg-amber-100 text-amber-800',
          icon: 'text-amber-500',
          pulse: ''
        };
      default:
        return {
          bg: 'bg-blue-50 border-blue-200',
          text: 'text-blue-700',
          badge: 'bg-blue-100 text-blue-800',
          icon: 'text-blue-500',
          pulse: ''
        };
    }
  };

  // Determine header gradient based on severity
  const getHeaderGradient = () => {
    if (summary.critical > 0) return 'from-red-500 to-rose-600';
    if (summary.warning > 0) return 'from-amber-500 to-orange-600';
    return 'from-green-500 to-emerald-600';
  };

  if (loading) {
    return (
      <div className="mb-4">
        <div className="w-full bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-lg p-4 shadow-md">
          <div className="flex items-center gap-3">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading connectivity alerts...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      {/* Collapsible Header Button - Same style as Network Dashboard */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full bg-gradient-to-r ${getHeaderGradient()} text-white rounded-lg p-4 hover:shadow-lg transition-all shadow-md`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <WifiOff className="h-6 w-6" />
              {summary.critical > 0 && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
              )}
            </div>
            <div className="text-left">
              <h2 className="text-xl font-bold">Connectivity Alerts</h2>
              <p className="text-sm text-white/80">Real-time customer monitoring</p>
            </div>
          </div>
          
          {/* Summary Stats in Header */}
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-3">
              {summary.total_inactive === 0 ? (
                <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="h-4 w-4" />
                  <span>All Online</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                    <span className="font-bold">{summary.total_inactive}</span>
                    <span>Inactive</span>
                  </div>
                  {summary.critical > 0 && (
                    <div className="flex items-center gap-2 bg-white/30 px-3 py-1 rounded-full text-sm animate-pulse">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-bold">{summary.critical}</span>
                      <span>Critical</span>
                    </div>
                  )}
                  {summary.with_tickets > 0 && (
                    <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                      <Ticket className="h-4 w-4" />
                      <span>{summary.with_tickets}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Mobile summary */}
            <div className="md:hidden flex items-center gap-2">
              {summary.total_inactive > 0 ? (
                <span className="bg-white/20 px-2 py-1 rounded-full text-sm font-bold">
                  {summary.total_inactive} Inactive
                </span>
              ) : (
                <span className="bg-white/20 px-2 py-1 rounded-full text-sm">
                  ✓ All Online
                </span>
              )}
            </div>
            
            {/* Last Updated */}
            {lastUpdated && (
              <span className="hidden lg:block text-xs text-white/70">
                {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            
            {/* Expand/Collapse Arrow */}
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronUp className="h-6 w-6" />
              ) : (
                <ChevronDown className="h-6 w-6" />
              )}
            </div>
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden animate-fadeIn">
          {/* Summary Stats Row */}
          <div className="grid grid-cols-4 gap-0 border-b bg-gray-50">
            <div className="p-3 text-center border-r">
              <p className="text-2xl font-bold text-gray-800">{summary.total_inactive}</p>
              <p className="text-xs text-gray-500">Total Inactive</p>
            </div>
            <div className={`p-3 text-center border-r ${summary.critical > 0 ? 'bg-red-50' : ''}`}>
              <p className={`text-2xl font-bold ${summary.critical > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {summary.critical}
              </p>
              <p className="text-xs text-gray-500">Critical (1h+)</p>
            </div>
            <div className={`p-3 text-center border-r ${summary.warning > 0 ? 'bg-amber-50' : ''}`}>
              <p className={`text-2xl font-bold ${summary.warning > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                {summary.warning}
              </p>
              <p className="text-xs text-gray-500">Warning (30m+)</p>
            </div>
            <div className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{summary.with_tickets}</p>
              <p className="text-xs text-gray-500">Auto Tickets</p>
            </div>
          </div>

          {/* Alerts List */}
          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">All Customers Online</p>
                <p className="text-gray-400 text-sm mt-1">No connectivity issues detected</p>
              </div>
            ) : (
              alerts.map((alert, index) => {
                const styles = getSeverityStyles(alert.severity);
                return (
                  <div 
                    key={alert.customer_id || index}
                    className={`p-3 border-b last:border-b-0 ${styles.bg} ${styles.pulse}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`p-1.5 rounded-full ${styles.badge} flex-shrink-0`}>
                          {alert.severity === 'critical' ? (
                            <AlertTriangle className="h-4 w-4" />
                          ) : (
                            <WifiOff className="h-4 w-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-semibold text-sm ${styles.text} truncate`}>
                              {alert.customer_name}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
                              {alert.severity.toUpperCase()}
                            </span>
                            {alert.has_ticket && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                                <Ticket className="h-3 w-3" />
                                {alert.ticket_info?.ticket_id}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Down {formatDuration(getLiveMinutes(alert))}
                            </span>
                            {alert.ip_address && alert.ip_address !== 'N/A' && (
                              <span className="font-mono">{alert.ip_address}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {onViewCustomer && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewCustomer(alert.customer_id);
                            }}
                            className="h-8 w-8 p-0 hover:bg-white/50"
                            title="View Customer"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleResolve(alert.customer_id, e)}
                          disabled={resolving === alert.customer_id}
                          className="h-8 w-8 p-0 hover:bg-green-100 text-green-600"
                          title="Mark as Resolved"
                        >
                          {resolving === alert.customer_id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-3 bg-gray-50 border-t flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Auto-refresh every {refreshInterval / 1000}s
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchAlerts}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh Now
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InactiveCustomerAlerts;
